import React, { useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import "../Accounting/FixedAssets/fixedAssets.css";

export default function StockOnHand() {
  const { companyId } = useCompany();
  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;

    const { data: wh } = await supabase
      .from("warehouses")
      .select("id,name,is_default")
      .eq("company_id", companyId)
      .order("name", { ascending: true });

    setWarehouses(wh || []);
    const def = (wh || []).find((w) => w.is_default);
    if (!warehouseId && def?.id) setWarehouseId(def.id);

    const { data, error } = await supabase
      .from("inventory_balances")
      .select(
        "warehouse_id,item_id,qty_on_hand,avg_cost, inventory_items:inventory_items(id,sku,name,unit,buffer_stock,reorder_level)"
      )
      .eq("company_id", companyId);

    if (!error) setRows(data || []);
  }, [companyId, warehouseId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!warehouseId) return rows;
    return rows.filter((r) => r.warehouse_id === warehouseId);
  }, [rows, warehouseId]);

  return (
    <div className="fa-wrap">
      <div className="fa-header">
        <div className="fa-title">
          <h2>Stock On Hand</h2>
          <p>On-hand quantity and average cost per item (balances table).</p>
        </div>
        <div className="fa-actions">
          <select
            className="fa-select"
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
          >
            <option value="">All warehouses</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
                {w.is_default ? " (Default)" : ""}
              </option>
            ))}
          </select>
          <button className="fa-btn" onClick={load}>
            Refresh
          </button>
        </div>
      </div>

      <div className="fa-panel">
        <table className="fa-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Item</th>
              <th>Qty</th>
              <th>Unit</th>
              <th>Avg Cost</th>
              <th>Value</th>
              <th>Buffer/Reorder</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const it = r.inventory_items;
              const qty = Number(r.qty_on_hand || 0);
              const avg = Number(r.avg_cost || 0);
              const val = qty * avg;
              const threshold = Math.max(
                Number(it?.buffer_stock || 0),
                Number(it?.reorder_level || 0)
              );
              const low = threshold > 0 && qty <= threshold;
              return (
                <tr
                  key={`${r.warehouse_id}-${r.item_id}`}
                  style={
                    low ? { background: "rgba(255,200,0,0.08)" } : undefined
                  }
                >
                  <td>{it?.sku}</td>
                  <td style={{ fontWeight: 900 }}>{it?.name}</td>
                  <td>{qty}</td>
                  <td>{it?.unit}</td>
                  <td>{avg.toFixed(2)}</td>
                  <td>{val.toFixed(2)}</td>
                  <td>{threshold}</td>
                  <td>{low ? "Low stock" : "OK"}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} style={{ opacity: 0.75 }}>
                  No balances found. Post a Stock IN movement to begin.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
